import React from "react";
import { Link } from "react-router-dom";
import { CheckCircleIcon } from "./Icons";

function VerificationGate({ readiness, title = "Complete Your Seller Verification" }) {
  const reqs = readiness?.requirements || {};
  const isSuspended = reqs.isSuspended ?? false;
  const aadhaarStatus = reqs.aadhaarStatus || "not_submitted";
  const panStatus = reqs.panStatus || "not_submitted";
  const bankStatus = reqs.bankStatus || "not_submitted";
  const progressPct = reqs.progressPercentage || 25;
  const completedSteps = reqs.completedSteps || 1;
  const totalSteps = reqs.totalSteps || 4;

  const renderBadge = (status) => {
    switch (status) {
      case "verified":
        return (
          <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ✓ Verified
          </span>
        );
      case "under_review":
      case "pending":
        return (
          <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ⏳ Under Review
          </span>
        );
      case "rejected":
      case "requires_reupload":
      case "requires_update":
        return (
          <span style={{ background: "#fee2e2", color: "#991b1b", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ⚠ Action Required
          </span>
        );
      default:
        return (
          <span style={{ background: "#fee2e2", color: "#b91c1c", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            ● Required
          </span>
        );
    }
  };

  if (isSuspended) {
    return (
      <div className="card-brand p-5 text-center" style={{ maxWidth: "640px", margin: "40px auto", border: "1.5px solid #fecaca", background: "#fef2f2" }}>
        <div style={{ fontSize: "44px", marginBottom: "14px" }}>🚫</div>
        <h3 style={{ fontSize: "20px", fontWeight: "900", color: "#991b1b", marginBottom: "8px" }}>
          Seller Account Suspended
        </h3>
        <p style={{ fontSize: "14px", color: "#7f1d1d", marginBottom: "20px", lineHeight: "1.6" }}>
          Your seller account has been temporarily suspended by marketplace administrators. Product listing and order management operations are currently locked.
        </p>
        <Link to="/vendor/support" className="btn btn-primary" style={{ fontSize: "13px", padding: "8px 24px" }}>
          Contact Merchant Support &rarr;
        </Link>
      </div>
    );
  }

  return (
    <div className="card-brand" style={{ maxWidth: "780px", margin: "30px auto", padding: "32px", border: "1.5px solid #e0e4ea", boxShadow: "0 10px 30px rgba(0,0,0,0.04)" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "28px" }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            background: "#eff6ff",
            color: "#1d4ed8",
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontSize: "24px",
            margin: "0 auto 16px"
          }}
        >
          🔒
        </div>
        <span style={{ fontSize: "12px", fontWeight: "800", letterSpacing: "1px", color: "var(--primary)", textTransform: "uppercase" }}>
          MANDATORY ONBOARDING REQUIREMENT
        </span>
        <h3 style={{ fontSize: "22px", fontWeight: "900", color: "#111315", marginTop: "4px", marginBottom: "8px" }}>
          {title}
        </h3>
        <p style={{ fontSize: "14px", color: "var(--text-muted)", maxWidth: "560px", margin: "0 auto", lineHeight: "1.5" }}>
          Your seller application is approved 🎉 To maintain marketplace trust and enable customer payouts, complete official identity and bank verification before listing products.
        </p>
      </div>

      {/* Progress Track */}
      <div style={{ background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "12px", padding: "16px 20px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", fontSize: "13px", fontWeight: "800" }}>
          <span style={{ color: "#111315" }}>Verification Progress</span>
          <span style={{ color: "var(--primary)" }}>{completedSteps} of {totalSteps} Completed ({progressPct}%)</span>
        </div>
        <div style={{ background: "#e2e8f0", height: "8px", borderRadius: "99px", overflow: "hidden" }}>
          <div style={{ background: "var(--primary)", width: `${progressPct}%`, height: "100%", transition: "width 0.4s ease" }} />
        </div>
      </div>

      {/* Checklist items */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "28px" }}>
        {/* Step 1 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <CheckCircleIcon size={18} color="#16a34a" />
            <div>
              <strong style={{ fontSize: "14px", color: "#111315", display: "block" }}>1. Seller Application</strong>
              <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>Admin approved your marketplace seller account.</span>
            </div>
          </div>
          <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: "99px", fontSize: "11.5px", fontWeight: "800" }}>
            ✓ Approved
          </span>
        </div>

        {/* Step 2 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "16px" }}>🪪</div>
            <div>
              <strong style={{ fontSize: "14px", color: "#111315", display: "block" }}>2. Aadhaar Card Verification</strong>
              <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>Official proof of identity for merchant validation.</span>
            </div>
          </div>
          {renderBadge(aadhaarStatus)}
        </div>

        {/* Step 3 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "16px" }}>📄</div>
            <div>
              <strong style={{ fontSize: "14px", color: "#111315", display: "block" }}>3. PAN Card Verification</strong>
              <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>Permanent Account Number for tax compliance.</span>
            </div>
          </div>
          {renderBadge(panStatus)}
        </div>

        {/* Step 4 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "16px" }}>🏦</div>
            <div>
              <strong style={{ fontSize: "14px", color: "#111315", display: "block" }}>4. Bank Account &amp; Payout Details</strong>
              <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>Required to receive direct order settlement earnings.</span>
            </div>
          </div>
          {renderBadge(bankStatus)}
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
        <Link
          to="/vendor/verification"
          className="btn btn-primary"
          style={{ fontSize: "13.5px", padding: "10px 28px", fontWeight: "800" }}
        >
          Complete Verification &rarr;
        </Link>
        <Link
          to="/vendor/dashboard"
          className="btn btn-outline-secondary"
          style={{ fontSize: "13.5px", padding: "10px 20px" }}
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

export default VerificationGate;
